import { Input, TextContent } from '@amzn/awsui-components-core';
import { BaseKeyDetail } from '@amzn/awsui-components-core/internal/events';

import { ReplyViewModel } from './CommentCardViewModel';
import { useReplyViewModel } from './ReplyViewModel';
import styles from './ReplyView.module.scss';

export interface ReplyViewProps {
  readonly threadId: string;
  readonly replies: readonly ReplyViewModel[];
  readonly isExpanded: boolean;
}

/** Displays the reply thread and input for a comment card. */
export function ReplyView({ threadId, replies, isExpanded }: ReplyViewProps) {
  const vm = useReplyViewModel(threadId);

  if (!isExpanded && replies.length > 0) {
    return (
      <div className={styles.repliesCount}>
        {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
      </div>
    );
  }

  if (!isExpanded) return null;

  return (
    <>
      {replies.length > 0 && (
        <div className={styles.repliesList}>
          {replies.map((reply) => (
            <div
              key={reply.commentId}
              className={`${styles.replyItem} ${reply.isOptimisticReply ? styles.replyPending : ''}`}
            >
              <div className={styles.replyMeta}>
                <TextContent>
                  <span className={styles.author}>{reply.author}</span>
                  <span className={styles.dot}>&nbsp; &middot; &nbsp;</span>
                  <span className={styles.time}>
                    {reply.isOptimisticReply ? 'Sending...' : reply.relativeTime}
                  </span>
                </TextContent>
              </div>
              <div className={styles.replyBody}>
                <TextContent>{reply.body}</TextContent>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.replyInput} onClick={(e) => e.stopPropagation()}>
        <Input
          value={vm.replyText}
          onChange={({ detail }) => vm.setReplyText(detail.value)}
          placeholder="Reply to thread..."
          onKeyDown={(e: CustomEvent<BaseKeyDetail>) => {
            if (e.detail.key === 'Enter') vm.submitReply();
          }}
        />
      </div>
    </>
  );
}
